import React from 'react';
import { Drawer, Button, Tag, Divider, Typography, Alert, Collapse } from 'antd';
import {
  GlobalOutlined,
  LinkedinOutlined,
  MailOutlined,
  ExportOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { ArchivedApplicationListItem, ArchiveReason, LLMRating } from '../../types';
import { renderBulletList } from '../../utils/renderUtils';
import SourceTag from '../common/SourceTag';
import css from './ArchivedApplicationDrawer.module.scss';

const { Title, Text, Paragraph } = Typography;

interface ArchivedApplicationDrawerProps {
  archive: ArchivedApplicationListItem | null;
  open: boolean;
  onClose: () => void;
  onUnarchive: (archive: ArchivedApplicationListItem) => void;
}

const ratingConfig: Record<LLMRating, { label: string; color: string }> = {
  perfect_fit: { label: 'Perfect Fit', color: '#52c41a' },
  strong_alignment: { label: 'Strong Alignment', color: '#73d13d' },
  potential_candidate: { label: 'Potential Candidate', color: '#faad14' },
  partial_fit: { label: 'Partial Fit', color: '#fa8c16' },
  misalignment: { label: 'Misalignment', color: '#ff4d4f' },
};

const archiveReasonConfig: Record<ArchiveReason, { label: string; color: string }> = {
  accepted_offer: { label: 'Accepted Offer', color: 'green' },
  employer_rejected: { label: 'Employer Rejected', color: 'red' },
  no_response: { label: 'No Response', color: 'default' },
  position_filled: { label: 'Position Filled', color: 'orange' },
  contact_unresponsive: { label: 'Contact Unresponsive', color: 'purple' },
  other: { label: 'Other', color: 'blue' },
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const ArchivedApplicationDrawer: React.FC<ArchivedApplicationDrawerProps> = ({
  archive,
  open,
  onClose,
  onUnarchive,
}) => {
  if (!archive) return null;

  const { opportunity, company, contact } = archive;
  const rating = opportunity.llmRating ? ratingConfig[opportunity.llmRating] : null;
  const reasonConfig = archiveReasonConfig[archive.archiveReason];
  const reasonLabel = archive.archiveReason === 'other' && archive.archiveReasonText
    ? archive.archiveReasonText
    : reasonConfig.label;

  return (
    <Drawer
      title={null}
      placement="right"
      width="min(560px, 94vw)"
      onClose={onClose}
      open={open}
      className={css.archivedDrawer}
      footer={
        <div className={css.footer}>
          <Button onClick={onClose}>Close</Button>
          <div className={css.footerActions}>
            <Button
              icon={<ExportOutlined />}
              onClick={() => window.open(opportunity.jobPostingUrl, '_blank')}
            >
              View Job Posting
            </Button>
            <Button
              icon={<UndoOutlined />}
              onClick={() => onUnarchive(archive)}
            >
              Unarchive
            </Button>
          </div>
        </div>
      }
    >
      {/* Header */}
      <div className={css.header}>
        <div className={css.titleRow}>
          <Title level={4} className={css.companyName}>
            {company.name}
          </Title>
        </div>
        <Text className={css.jobTitle}>{opportunity.title}</Text>
        <div className={css.tags}>
          <SourceTag source={opportunity.source} sourceSite={opportunity.sourceSite} />
          <Tag color={reasonConfig.color}>{reasonLabel}</Tag>
          {rating && <Tag color={rating.color}>{rating.label}</Tag>}
          <span className={css.similarity}>
            {Math.round(opportunity.similarityScore * 100)}% match
          </span>
        </div>
      </div>

      <Alert
        type="info"
        showIcon
        className={css.calloutBanner}
        message={`Archived on ${formatDate(archive.archivedAt)} \u00b7 Applied on ${formatDate(archive.submittedAt)}`}
      />

      <Divider />

      {/* Contact */}
      {contact && (
        <section className={css.section}>
          <Title level={5}>Contact</Title>
          <div className={css.contactCard}>
            <div className={css.contactInfo}>
              <Text strong>
                {contact.firstName} {contact.lastName}
              </Text>
              <Text type="secondary">{contact.position}</Text>
              <div className={css.email}>
                <MailOutlined />
                <Text copyable={{ text: contact.email }}>
                  {contact.email}
                </Text>
              </div>
            </div>
            {contact.linkedinUrl && (
              <a
                href={contact.linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={css.contactLinkedin}
              >
                <LinkedinOutlined />
              </a>
            )}
          </div>
        </section>
      )}

      <Divider />

      {/* Fit Assessment */}
      <section className={css.section}>
        <Title level={5}>Why You Were a Good Fit</Title>
        {renderBulletList(opportunity.suitabilityReason, 'No detailed explanation available.')}
      </section>

      <Divider />

      <Collapse
        ghost
        className={css.detailsCollapse}
        items={[
          ...(opportunity.summary
            ? [
                {
                  key: 'summary',
                  label: 'Job Summary',
                  children: (
                    <section className={css.section}>
                      <Paragraph>{opportunity.summary.generalOverview}</Paragraph>

                      {opportunity.summary.keyResponsibilities && (
                        <div className={css.listSection}>
                          <Text strong>Key Responsibilities:</Text>
                          <ul>
                            {opportunity.summary.keyResponsibilities.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {opportunity.summary.requiredQualifications && (
                        <div className={css.listSection}>
                          <Text strong>Required Qualifications:</Text>
                          <ul>
                            {opportunity.summary.requiredQualifications.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </section>
                  ),
                },
              ]
            : []),
          {
            key: 'company',
            label: `About ${company.name}`,
            children: (
              <section className={css.section}>
                <Paragraph>{company.description}</Paragraph>
                <div className={css.links}>
                  {company.websiteUrl && (
                    <a href={company.websiteUrl} target="_blank" rel="noopener noreferrer">
                      <GlobalOutlined /> Website
                    </a>
                  )}
                  {company.linkedinUrl && (
                    <a href={company.linkedinUrl} target="_blank" rel="noopener noreferrer">
                      <LinkedinOutlined /> LinkedIn
                    </a>
                  )}
                </div>
              </section>
            ),
          },
        ]}
      />
    </Drawer>
  );
};

export default ArchivedApplicationDrawer;
